/**
 * 1.3.0 the media portal's sign-in screens (stream P1's): sign-in, the two-step code, enrolment (with the recovery
 * codes), forgot password, set a password (from an invitation or a reset link) and the account page. Every string
 * comes from the catalogue through t(); no inline styles (the portal's CSP allows none), only identity.css.
 */
import { useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';
import type { PortalMeDTO, PortalPublicDTO, PortalSignInResult, PortalTotpConfirmDTO, PortalTotpEnrolDTO } from '@prism/contracts';
import { portalApi, PortalApiError } from '../client';
import { t } from '../i18n/en';
import './identity.css';

/** What a failed call says to the supplier. */
export function problemText(err: unknown): string {
  if (err instanceof PortalApiError) {
    if (err.status === 429) return t('identity.tooMany', { seconds: Number(err.details?.retryAfter ?? 30) });
    if (err.code === 'INVALID_CREDENTIALS') return t('identity.badCredentials');
    if (err.code === 'WRONG_CODE') return err.message || t('identity.code.wrong');
    if (err.code === 'PORTAL_DISABLED') return t('error.PORTAL_DISABLED');
    if (err.code === 'BAD_REQUEST') return t('common.error');
    return err.message;
  }
  return t('common.error');
}

const portalNameOf = (pub: PortalPublicDTO | null) => pub?.branding.portalName ?? t('common.appName');

/**
 * The frame every sign-in screen sits in: the portal's logo (1.3.1) and name above a card. The accent colour (1.3.1)
 * is set as a custom property through the CSSOM (allowed by the portal's CSP, unlike a style attribute).
 */
export function AuthFrame({ pub, wide, children, testId }: { pub: PortalPublicDTO | null; wide?: boolean; children: ReactNode; testId?: string }) {
  const root = useRef<HTMLDivElement>(null);
  const accent = pub?.branding.accentColor ?? null;
  const logoUrl = pub?.branding.logoUrl ?? null;
  const [logoFailed, setLogoFailed] = useState(false);
  useEffect(() => {
    const el = root.current;
    if (!el) return;
    if (accent && /^#[0-9a-fA-F]{6}$/.test(accent)) el.style.setProperty('--pid-brand-accent', accent);
    else el.style.removeProperty('--pid-brand-accent');
  }, [accent]);
  return (
    <div ref={root} className={accent ? 'pid pid-screen pid-branded' : 'pid pid-screen'}>
      <main className={wide ? 'pid-card wide' : 'pid-card'} data-testid={testId}>
        {logoUrl && !logoFailed && (
          <div className="pid-logo"><img src={logoUrl} alt={portalNameOf(pub)} onError={() => setLogoFailed(true)} data-testid="portal-sign-in-logo" /></div>
        )}
        <p className="pid-brand">{portalNameOf(pub)}</p>
        {children}
      </main>
    </div>
  );
}

function useSubmit<T>(run: () => Promise<T>, done: (r: T) => void) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const submit = async (e?: FormEvent) => {
    e?.preventDefault();
    setBusy(true);
    setError(null);
    try {
      done(await run());
    } catch (err) {
      setError(problemText(err));
    } finally {
      setBusy(false);
    }
  };
  return { busy, error, submit };
}

/* ------------------------------------------------------------ sign-in */

export function SignInScreen({ pub, onResult, onForgot, notice }: { pub: PortalPublicDTO | null; onResult: (r: PortalSignInResult) => void; onForgot: () => void; notice?: string | null }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const { busy, error, submit } = useSubmit(
    () => portalApi<PortalSignInResult>('/auth/login', { method: 'POST', json: { email: email.trim(), password } }),
    onResult,
  );
  return (
    <AuthFrame pub={pub} testId="portal-sign-in">
      <h1>{t('identity.signInTitle', { portalName: portalNameOf(pub) })}</h1>
      {pub?.branding.welcomeText ? <p className="pid-welcome">{pub.branding.welcomeText}</p> : <p className="pid-lede">{t('identity.signInLede')}</p>}
      {notice && <p className="pid-ok" role="status">{notice}</p>}
      <form className="pid-form" onSubmit={(e) => void submit(e)}>
        <div className="pid-field">
          <label htmlFor="pid-email">{t('identity.email')}</label>
          <input id="pid-email" className="pid-input" type="email" autoComplete="username" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="portal-email" />
        </div>
        <div className="pid-field">
          <label htmlFor="pid-password">{t('identity.password')}</label>
          <input id="pid-password" className="pid-input" type="password" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="portal-password" />
        </div>
        {error && <p className="pid-error" role="alert" data-testid="portal-sign-in-error">{error}</p>}
        <button className="pid-button primary" type="submit" disabled={busy || !email || !password} data-testid="portal-sign-in-submit">{busy ? t('identity.signingIn') : t('identity.signIn')}</button>
      </form>
      <button className="pid-link" type="button" onClick={onForgot} data-testid="portal-forgot-link">{t('identity.forgot')}</button>
      {pub?.branding.helpText && <p className="pid-help-text">{pub.branding.helpText}</p>}
    </AuthFrame>
  );
}

/* ------------------------------------------------------------ the two-step code */

export function CodeScreen({ pub, onResult, onBack }: { pub: PortalPublicDTO | null; onResult: (r: PortalSignInResult) => void; onBack: () => void }) {
  const [recovery, setRecovery] = useState(false);
  const [code, setCode] = useState('');
  const { busy, error, submit } = useSubmit(
    () => portalApi<PortalSignInResult>('/auth/code', { method: 'POST', json: { code: code.trim() } }),
    onResult,
  );
  return (
    <AuthFrame pub={pub} testId="portal-code">
      <h1>{t('identity.code.title')}</h1>
      <p className="pid-lede">{recovery ? t('identity.enrol.recoveryLede') : t('identity.code.lede')}</p>
      <form className="pid-form" onSubmit={(e) => void submit(e)}>
        <div className="pid-field">
          <label htmlFor="pid-code">{recovery ? t('identity.code.recovery') : t('identity.code')}</label>
          <input id="pid-code" className={recovery ? 'pid-input' : 'pid-input code'} autoComplete="one-time-code" inputMode={recovery ? 'text' : 'numeric'}
            autoFocus required value={code} onChange={(e) => setCode(e.target.value)} data-testid="portal-code-input" />
        </div>
        {error && <p className="pid-error" role="alert">{error}</p>}
        <button className="pid-button primary" type="submit" disabled={busy || code.trim().length < 6} data-testid="portal-code-submit">{t('identity.code.verify')}</button>
      </form>
      <button className="pid-link" type="button" onClick={() => { setRecovery(!recovery); setCode(''); }}>{recovery ? t('identity.code.useApp') : t('identity.code.useRecovery')}</button>
      <button className="pid-link" type="button" onClick={onBack}>{t('identity.backToSignIn')}</button>
    </AuthFrame>
  );
}

/* ------------------------------------------------------------ enrolment */

/** Scans the QR code, confirms a code, then shows the recovery codes once; `onDone` after they are saved. */
export function EnrolPanel({ onDone, onCancel }: { onDone: () => void; onCancel?: () => void }) {
  const [enrol, setEnrol] = useState<PortalTotpEnrolDTO | null>(null);
  const [startError, setStartError] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [codes, setCodes] = useState<string[] | null>(null);
  const start = async () => {
    setStartError(null);
    try {
      setEnrol(await portalApi<PortalTotpEnrolDTO>('/auth/enrol', { method: 'POST', json: {} }));
    } catch (err) {
      setStartError(problemText(err));
    }
  };
  useEffect(() => { void start(); }, []);
  const { busy, error, submit } = useSubmit(
    () => portalApi<PortalTotpConfirmDTO>('/auth/enrol/confirm', { method: 'POST', json: { code: code.trim() } }),
    (r) => setCodes(r.recoveryCodes),
  );
  if (codes) {
    return (
      <div className="pid-form" data-testid="portal-recovery-codes">
        <h2>{t('identity.enrol.recoveryTitle')}</h2>
        <p className="pid-lede">{t('identity.enrol.recoveryLede')}</p>
        <ul className="pid-codes">{codes.map((c) => <li key={c}>{c}</li>)}</ul>
        <button className="pid-button primary" type="button" onClick={onDone} data-testid="portal-recovery-done">{t('identity.enrol.done')}</button>
      </div>
    );
  }
  return (
    <div className="pid-form" data-testid="portal-enrol">
      <p className="pid-lede">{t('identity.enrol.scan')}</p>
      {startError && <p className="pid-error" role="alert">{startError}</p>}
      {enrol && (
        <>
          <img className="pid-qr" alt="" src={`data:image/svg+xml;base64,${btoa(enrol.qrSvg)}`} />
          <div className="pid-field">
            <label>{t('identity.enrol.key')}</label>
            <p className="pid-key" data-testid="portal-enrol-secret">{enrol.secret.match(/.{1,4}/g)!.join(' ')}</p>
          </div>
          <form className="pid-form" onSubmit={(e) => void submit(e)}>
            <div className="pid-field">
              <label htmlFor="pid-enrol-code">{t('identity.enrol.confirm')}</label>
              <input id="pid-enrol-code" className="pid-input code" inputMode="numeric" autoComplete="one-time-code" required value={code} onChange={(e) => setCode(e.target.value)} data-testid="portal-enrol-code" />
            </div>
            {error && <p className="pid-error" role="alert">{error}</p>}
            <div className="pid-row">
              <button className="pid-button primary" type="submit" disabled={busy || code.trim().length < 6} data-testid="portal-enrol-submit">{t('identity.continue')}</button>
              {onCancel && <button className="pid-button" type="button" onClick={onCancel}>{t('common.cancel')}</button>}
            </div>
          </form>
        </>
      )}
    </div>
  );
}

export function EnrolScreen({ pub, onDone, onBack }: { pub: PortalPublicDTO | null; onDone: () => void; onBack: () => void }) {
  return (
    <AuthFrame pub={pub} testId="portal-enrol-screen">
      <h1>{t('identity.enrol.title')}</h1>
      <p className="pid-lede">{t('identity.enrol.lede')}</p>
      <EnrolPanel onDone={onDone} />
      <button className="pid-link" type="button" onClick={onBack}>{t('identity.backToSignIn')}</button>
    </AuthFrame>
  );
}

/* ------------------------------------------------------------ forgot and set password */

export function ForgotScreen({ pub, onBack }: { pub: PortalPublicDTO | null; onBack: () => void }) {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const { busy, error, submit } = useSubmit(() => portalApi('/auth/forgot', { method: 'POST', json: { email: email.trim() } }), () => setSent(true));
  return (
    <AuthFrame pub={pub} testId="portal-forgot">
      <h1>{t('identity.forgot.title')}</h1>
      {sent ? <p className="pid-ok" role="status" data-testid="portal-forgot-sent">{t('identity.forgot.sent')}</p> : (
        <>
          <p className="pid-lede">{t('identity.forgot.lede')}</p>
          <form className="pid-form" onSubmit={(e) => void submit(e)}>
            <div className="pid-field">
              <label htmlFor="pid-forgot-email">{t('identity.email')}</label>
              <input id="pid-forgot-email" className="pid-input" type="email" autoComplete="username" required value={email} onChange={(e) => setEmail(e.target.value)} />
            </div>
            {error && <p className="pid-error" role="alert">{error}</p>}
            <button className="pid-button primary" type="submit" disabled={busy || !email.trim()}>{t('identity.forgot.send')}</button>
          </form>
        </>
      )}
      <button className="pid-link" type="button" onClick={onBack}>{t('identity.backToSignIn')}</button>
    </AuthFrame>
  );
}

/** A new password with a repeat; `onSave` gets it. */
function PasswordPair({ submitLabel, onSave, busy, error, current }: { submitLabel: string; onSave: (password: string, current: string) => void; busy: boolean; error: string | null; current?: boolean }) {
  const [old, setOld] = useState('');
  const [a, setA] = useState('');
  const [b, setB] = useState('');
  const mismatch = b.length > 0 && a !== b;
  return (
    <form className="pid-form" onSubmit={(e) => { e.preventDefault(); if (!mismatch) onSave(a, old); }}>
      {current && (
        <div className="pid-field">
          <label htmlFor="pid-current">{t('identity.account.current')}</label>
          <input id="pid-current" className="pid-input" type="password" autoComplete="current-password" required value={old} onChange={(e) => setOld(e.target.value)} data-testid="portal-current-password" />
        </div>
      )}
      <div className="pid-field">
        <label htmlFor="pid-new">{t('identity.setPassword.new')}</label>
        <input id="pid-new" className="pid-input" type="password" autoComplete="new-password" minLength={12} required value={a} onChange={(e) => setA(e.target.value)} data-testid="portal-new-password" />
        <p className="pid-help">{t('identity.setPassword.hint')}</p>
      </div>
      <div className="pid-field">
        <label htmlFor="pid-repeat">{t('identity.setPassword.repeat')}</label>
        <input id="pid-repeat" className="pid-input" type="password" autoComplete="new-password" required value={b} onChange={(e) => setB(e.target.value)} data-testid="portal-repeat-password" />
      </div>
      {mismatch && <p className="pid-error">{t('identity.setPassword.mismatch')}</p>}
      {error && <p className="pid-error" role="alert">{error}</p>}
      <button className="pid-button primary" type="submit" disabled={busy || a.length < 12 || a !== b || (current && !old)} data-testid="portal-password-submit">{submitLabel}</button>
    </form>
  );
}

/** From an invitation (signs in) or a reset link (back to sign-in). */
export function SetPasswordScreen({ pub, mode, token, onInvitation, onReset, onBack }: {
  pub: PortalPublicDTO | null; mode: 'invitation' | 'reset'; token: string;
  onInvitation: (r: PortalSignInResult) => void; onReset: () => void; onBack: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const save = async (password: string) => {
    setBusy(true);
    setError(null);
    try {
      const r = await portalApi<PortalSignInResult | { ok: true }>(mode === 'invitation' ? '/auth/invitation' : '/auth/reset', { method: 'POST', json: { token, password } });
      if (mode === 'invitation') onInvitation(r as PortalSignInResult); else onReset();
    } catch (err) {
      setError(problemText(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <AuthFrame pub={pub} testId="portal-set-password">
      <h1>{t('identity.setPassword.title')}</h1>
      <p className="pid-lede">{mode === 'invitation' ? t('identity.setPassword.inviteLede') : t('identity.setPassword.resetLede')}</p>
      <PasswordPair submitLabel={t('identity.setPassword.save')} busy={busy} error={error} onSave={(p) => void save(p)} />
      <button className="pid-link" type="button" onClick={onBack}>{t('identity.backToSignIn')}</button>
    </AuthFrame>
  );
}

/* ------------------------------------------------------------ the account page */

interface TwoStepStatus { enabled: boolean; required: boolean; recoveryCodesLeft: number }

/** Your account: who you are, the password, two-step sign-in and sign-out. */
export function AccountPage({ me, onSignOut, onChanged }: { me: PortalMeDTO; onSignOut: () => void; onChanged?: () => void }) {
  const [status, setStatus] = useState<TwoStepStatus | null>(null);
  const [enrolling, setEnrolling] = useState(false);
  const [disabling, setDisabling] = useState(false);
  const [code, setCode] = useState('');
  const [changed, setChanged] = useState(false);
  const load = () => portalApi<TwoStepStatus>('/me/two-step').then(setStatus).catch(() => setStatus(null));
  useEffect(() => { void load(); }, []);
  const [pwBusy, setPwBusy] = useState(false);
  const [pwError, setPwError] = useState<string | null>(null);
  const savePassword = async (newPassword: string, currentPassword: string) => {
    setPwBusy(true);
    setPwError(null);
    try {
      await portalApi('/me/password', { method: 'POST', json: { currentPassword, newPassword } });
      setChanged(true);
    } catch (err) {
      setPwError(problemText(err));
    } finally {
      setPwBusy(false);
    }
  };
  const off = useSubmit(() => portalApi('/me/two-step/disable', { method: 'POST', json: { code: code.trim() } }), () => { setDisabling(false); setCode(''); void load(); onChanged?.(); });
  return (
    <section className="pid pid-account" data-testid="portal-account">
      <h1>{t('identity.account.title')}</h1>
      <dl className="pid-dl">
        <dt>{t('identity.email')}</dt><dd>{me.email}</dd>
        <dt>{t('identity.account.company')}</dt><dd>{me.company.name}</dd>
      </dl>

      <div className="pid-section">
        <h2>{t('identity.account.passwordTitle')}</h2>
        {changed ? <p className="pid-ok" role="status" data-testid="portal-password-changed">{t('identity.account.changed')}</p>
          : <PasswordPair current submitLabel={t('identity.account.change')} busy={pwBusy} error={pwError} onSave={(p, old) => void savePassword(p, old)} />}
      </div>

      <div className="pid-section" data-testid="portal-account-two-step">
        <h2>{t('identity.account.twoStepTitle')}</h2>
        {status && <p className="pid-muted">{status.enabled ? t('identity.account.twoStepOn') : t('identity.account.twoStepOff')} {status.required ? t('identity.account.twoStepRequired') : ''}</p>}
        {status && !status.enabled && (enrolling
          ? <EnrolPanel onDone={() => { setEnrolling(false); void load(); onChanged?.(); }} onCancel={() => setEnrolling(false)} />
          : <div className="pid-row"><button className="pid-button" type="button" onClick={() => setEnrolling(true)}>{t('identity.account.twoStepEnable')}</button></div>)}
        {status?.enabled && !status.required && (disabling ? (
          <form className="pid-form" onSubmit={(e) => void off.submit(e)}>
            <div className="pid-field">
              <label htmlFor="pid-off-code">{t('identity.code')}</label>
              <input id="pid-off-code" className="pid-input code" inputMode="numeric" required value={code} onChange={(e) => setCode(e.target.value)} />
            </div>
            {off.error && <p className="pid-error" role="alert">{off.error}</p>}
            <div className="pid-row">
              <button className="pid-button" type="submit" disabled={off.busy || code.trim().length < 6}>{t('identity.account.twoStepDisable')}</button>
              <button className="pid-button" type="button" onClick={() => setDisabling(false)}>{t('common.cancel')}</button>
            </div>
          </form>
        ) : <div className="pid-row"><button className="pid-button" type="button" onClick={() => setDisabling(true)}>{t('identity.account.twoStepDisable')}</button></div>)}
      </div>

      <div className="pid-section">
        <h2>{t('identity.account.signOutTitle')}</h2>
        <p className="pid-muted">{t('identity.account.signOutLede')}</p>
        <div className="pid-row"><button className="pid-button" type="button" onClick={onSignOut} data-testid="portal-sign-out">{t('identity.signOut')}</button></div>
      </div>
    </section>
  );
}
